import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';

/** Forgot password: step 1 enter email (Cognito sends code), step 2 enter code + new password. */
export default function ForgotPassword() {
  const { t } = useTranslation();
  const { forgotPassword, confirmForgotPassword } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState<'email' | 'reset'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await forgotPassword(email.trim());
      setStep('reset');
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : typeof (err as { message?: string })?.message === 'string' ? (err as { message: string }).message : t('auth.forgotPasswordFailed');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError(t('auth.passwordsDoNotMatch'));
      return;
    }
    if (newPassword.length < 8) {
      setError(t('auth.passwordTooShort'));
      return;
    }
    setLoading(true);
    try {
      await confirmForgotPassword(email.trim(), code.trim(), newPassword);
      setSuccess(true);
      setTimeout(() => navigate('/login', { replace: true }), 2000);
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : typeof (err as { message?: string })?.message === 'string' ? (err as { message: string }).message : t('auth.resetPasswordFailed');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div style={{ maxWidth: 420, margin: '4rem auto', padding: '2rem', textAlign: 'center' }}>
        <div style={{ padding: '1rem', background: '#ecfdf5', color: '#065f46', borderRadius: 8, marginBottom: '1rem' }}>
          {t('auth.passwordResetSuccess')}
        </div>
        <p style={{ color: '#6b7280', fontSize: '0.95rem' }}>{t('auth.redirectingToLogin')}</p>
        <Link to="/login" style={{ color: '#0d9488', fontWeight: 600 }}>
          {t('auth.signIn')}
        </Link>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 420, margin: '4rem auto', padding: '2rem' }}>
      <h1 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: '1rem', color: '#111' }}>
        {step === 'email' ? t('auth.forgotPassword') : t('auth.resetPassword')}
      </h1>
      <p style={{ color: '#6b7280', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
        {step === 'email' ? t('auth.forgotPasswordDesc') : t('auth.resetPasswordDesc')}
      </p>

      {step === 'email' ? (
        <form onSubmit={handleRequestCode} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontWeight: 500, color: '#374151' }}>
            {t('auth.email')}
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              style={{ padding: '0.6rem 0.75rem', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '1rem' }}
            />
          </label>
          {error && (
            <div style={{ padding: '0.75rem', background: '#fef2f2', color: '#b91c1c', borderRadius: 8, fontSize: '0.9rem' }}>
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            style={{
              padding: '0.75rem 1.25rem',
              background: loading ? '#9ca3af' : '#0d9488',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontWeight: 600,
              fontSize: '1rem',
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? t('auth.sending') : t('auth.sendResetCode')}
          </button>
        </form>
      ) : (
        <form onSubmit={handleResetPassword} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <p style={{ fontSize: '0.9rem', color: '#6b7280' }}>{t('auth.checkEmailForCode')}</p>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontWeight: 500, color: '#374151' }}>
            {t('auth.verificationCode')}
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              autoComplete="one-time-code"
              placeholder="123456"
              style={{ padding: '0.6rem 0.75rem', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '1rem' }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontWeight: 500, color: '#374151' }}>
            {t('auth.newPassword')}
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              style={{ padding: '0.6rem 0.75rem', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '1rem' }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontWeight: 500, color: '#374151' }}>
            {t('auth.confirmNewPassword')}
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              style={{ padding: '0.6rem 0.75rem', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '1rem' }}
            />
          </label>
          {error && (
            <div style={{ padding: '0.75rem', background: '#fef2f2', color: '#b91c1c', borderRadius: 8, fontSize: '0.9rem' }}>
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            style={{
              padding: '0.75rem 1.25rem',
              background: loading ? '#9ca3af' : '#0d9488',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontWeight: 600,
              fontSize: '1rem',
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? t('auth.resetting') : t('auth.resetPasswordButton')}
          </button>
        </form>
      )}

      <p style={{ marginTop: '1.25rem', color: '#6b7280', fontSize: '0.9rem' }}>
        <Link to="/login" style={{ color: '#0d9488', fontWeight: 600 }}>
          ← {t('auth.backToSignIn')}
        </Link>
      </p>
      <Link to="/" style={{ display: 'inline-block', marginTop: '0.5rem', color: '#6b7280', fontSize: '0.9rem' }}>
        ← {t('auth.backToHome')}
      </Link>
    </div>
  );
}
